import { useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

// VIDEO FEATURE FLAG — temporary gate. While false, only the Audio option is
// offered (the dual video+audio recorder plumbing isn't built yet). Flip to true
// once that recorder lands so subjects can choose Video. Default: false.
const VIDEO_ENABLED = false;

type Medium = "video" | "audio";

type MediumChoiceProps = {
  sessionToken: string;
  currentMedium: Medium | null;
  onChosen: (medium: Medium) => void;
};

const MediumChoice = ({ sessionToken, currentMedium, onChosen }: MediumChoiceProps) => {
  // The button currently mid-write (drives its spinner / aria-busy); null = idle.
  const [pending, setPending] = useState<Medium | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Last attempted value, so the Retry button re-fires the same choice. Seeded
  // from currentMedium (the parent only mounts us when it's null, so this is a
  // harmless default that also documents the prop's contract).
  const [lastMedium, setLastMedium] = useState<Medium | null>(currentMedium);

  const choose = async (medium: Medium) => {
    setPending(medium);
    setLastMedium(medium);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        capture_medium?: Medium;
      }>("interview-set-medium", {
        body: { medium },
        headers: { Authorization: `Bearer ${sessionToken}` },
      });

      // 2xx: saved — advance with the server's value.
      if (!error && data?.ok && data.capture_medium) {
        onChosen(data.capture_medium);
        return;
      }

      // Non-2xx: supabase-js wraps the response on error.context. A 409 whose
      // body carries capture_medium means the choice is already locked server-
      // side (possibly to a different value) — treat the server as authoritative
      // and advance with the locked value rather than fighting it.
      if (error) {
        const context = (error as unknown as { context?: Response }).context;
        if (context && context.status === 409) {
          try {
            const body = (await context.clone().json()) as { capture_medium?: Medium };
            if (body.capture_medium) {
              onChosen(body.capture_medium);
              return;
            }
          } catch {
            // fall through to the generic recoverable error
          }
        }
      }

      // Anything else (window closed, raced, other 4xx/5xx, malformed 2xx): recoverable.
      setError("Could not save your choice — please try again.");
      setPending(null);
    } catch {
      setError("Could not save your choice — please try again.");
      setPending(null);
    }
  };

  const subtitle = VIDEO_ENABLED
    ? "Your family will be able to revisit this however you choose."
    : "Recording will be audio-only for now.";

  return (
    <div className="text-center">
      <h1 className="text-2xl font-display text-foreground">
        How would you like to be recorded?
      </h1>
      <p className="mt-3 text-base text-muted-foreground max-w-md mx-auto">{subtitle}</p>

      <div className="mt-8 flex flex-col gap-4 items-stretch max-w-sm mx-auto">
        {VIDEO_ENABLED ? (
          <button
            type="button"
            onClick={() => choose("video")}
            disabled={pending !== null}
            aria-busy={pending === "video"}
            className="rounded-2xl border-2 border-primary bg-primary/5 px-6 py-6 text-left transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          >
            <span className="flex items-center justify-between gap-3">
              <span className="text-xl font-semibold text-foreground">Video (recommended)</span>
              {pending === "video" ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : null}
            </span>
            <span className="mt-1 block text-sm text-muted-foreground">
              Your family will see and hear you.
            </span>
          </button>
        ) : null}

        <button
          type="button"
          onClick={() => choose("audio")}
          disabled={pending !== null}
          aria-busy={pending === "audio"}
          className="rounded-2xl border border-border px-6 py-5 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        >
          <span className="flex items-center justify-between gap-3">
            <span className="text-lg font-medium text-foreground">Audio only</span>
            {pending === "audio" ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : null}
          </span>
          <span className="mt-1 block text-sm text-muted-foreground">
            Just your voice, if you would prefer not to be on camera.
          </span>
        </button>
      </div>

      {error ? (
        <div className="mt-6">
          <p className="text-sm text-destructive">{error}</p>
          <Button
            size="lg"
            variant="outline"
            className="mt-3"
            onClick={() => lastMedium && choose(lastMedium)}
          >
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default MediumChoice;
